import React from "react";
import { CircleAlert } from "lucide-react";
import UseContext from "hooks/useContext";
import { TASK_NOTICE } from "pages/missionTemplates/utils/templateTasks";

// One pass, so a label containing "{label}" or "$&" is shown as typed.
const fillPlaceholder = (template, name, value) =>
    template.replace(`{${name}}`, () => value || "");

/** The message under the add field of the Tasks step (`templateData.taskNotice`). */
const TaskNotice = ({
    notice,
    showAddLink,
    onAdd,
    canAddLine,
    onAddLine,
}) => {
    const { language } = UseContext();
    const t = (key, fallback) =>
        language.getValue(`missionTemplate.tasks.${key}`, fallback);

    // Text of a notice, or of one line of a MULTIPLE notice.
    const messageOf = (item) => {
        switch (item?.type) {
            case TASK_NOTICE.TOO_MANY:
                return t("tooMany", "Le nombre maximum de tâches est atteint.");
            case TASK_NOTICE.REJECTED:
                return fillPlaceholder(
                    t(
                        "rejected",
                        "« {label} » ne décrit pas une tâche du poste."
                    ),
                    "label",
                    item.label
                );
            case TASK_NOTICE.UNCHECKED_TOO_LONG:
                return fillPlaceholder(
                    t(
                        "uncheckedTooLong",
                        "« {label} » n'a pas pu être vérifiée : ajoutez les tâches une par une (80 caractères au plus)."
                    ),
                    "label",
                    item.label
                );
            case TASK_NOTICE.TICKED:
                return fillPlaceholder(
                    t("existingTicked", "« {match} » existe déjà : elle a été cochée."),
                    "match",
                    item.match
                );
            case TASK_NOTICE.ALREADY_LISTED:
                return fillPlaceholder(
                    t(
                        "existingListed",
                        "« {match} » existe déjà dans la liste."
                    ),
                    "match",
                    item.match
                );
            default:
                return "";
        }
    };

    const renderAddLink = (onClick) => (
        <>
            {" "}
            <button
                type="button"
                onClick={onClick}
                className="underline hover:opacity-80 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
            >
                {t("addAsNew", "Ajouter comme nouvelle tâche")}
            </button>
        </>
    );

    // A flat banner, no border and no cross (typing the next task clears it, see TasksTemplate):
    // grey for information, red with an alert icon for an error (a task that was not added).
    const renderBanner = (content, tone = "neutral") => {
        const isError = tone === "error";
        return (
            <div
                className={`flex min-w-0 items-start gap-2 rounded-[0.4rem] px-3 py-2 ${
                    isError ? "bg-red-50" : "bg-gray-50"
                }`}
                role={isError ? "alert" : "status"}
            >
                {isError ? (
                    <CircleAlert size={16} color="#B91C1C" className="mt-[3px] shrink-0" aria-hidden="true" />
                ) : null}
                {content}
            </div>
        );
    };

    // Red: the task was not added (refused, too long, maximum reached, already in the list).
    const isErrorLine = (line) =>
        line.type === TASK_NOTICE.REJECTED ||
        line.type === TASK_NOTICE.TOO_MANY ||
        line.type === TASK_NOTICE.UNCHECKED_TOO_LONG ||
        line.type === TASK_NOTICE.ALREADY_LISTED;

    switch (notice?.type) {
        case TASK_NOTICE.TOO_MANY:
        case TASK_NOTICE.REJECTED:
        case TASK_NOTICE.UNCHECKED_TOO_LONG:
            return renderBanner(
                <p className="ft-sm text-red-700 min-w-0 break-words py-0.5">
                    {messageOf(notice)}
                </p>,
                "error"
            );
        case TASK_NOTICE.TICKED:
        case TASK_NOTICE.ALREADY_LISTED: {
            const isError = isErrorLine(notice);
            return renderBanner(
                <p className={`ft-sm min-w-0 break-words py-0.5 ${isError ? "text-red-700" : "text-gray-500"}`}>
                    {messageOf(notice)}
                    {showAddLink && renderAddLink(onAdd)}
                </p>,
                isError ? "error" : "neutral"
            );
        }
        case TASK_NOTICE.MULTIPLE: {
            const lines = Array.isArray(notice.lines) ? notice.lines : [];
            if (lines.length === 0) return null;
            // One banner per line: red when the task was not added, grey otherwise. The
            // errors come first.
            const ordered = [
                ...lines.filter(isErrorLine),
                ...lines.filter((line) => !isErrorLine(line)),
            ];
            return (
                <div className="flex flex-col gap-2">
                    {ordered.map((line) => {
                        const isError = isErrorLine(line);
                        return (
                            <React.Fragment key={line.id}>
                                {renderBanner(
                                    <p className={`ft-sm min-w-0 break-words py-0.5 ${isError ? "text-red-700" : "text-gray-500"}`}>
                                        {messageOf(line)}
                                        {canAddLine?.(line) &&
                                            renderAddLink(() => onAddLine(line.id))}
                                    </p>,
                                    isError ? "error" : "neutral"
                                )}
                            </React.Fragment>
                        );
                    })}
                </div>
            );
        }
        default:
            return null;
    }
};

export default TaskNotice;
